import { useState, useEffect } from "react";
import { Search, SortDesc, Calendar, LayoutGrid, List as ListIcon, TrendingUp, Loader2, ArrowLeft, HelpCircle, Filter, ArrowUpAZ, Clock, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Joyride, { CallBackProps, STATUS, Step, ACTIONS } from 'react-joyride';
import { TourTooltip } from '../common/TourTooltip';
import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import { PaperCode } from "./types";
import { fetchPaperCodes } from "./EvaluatorSlice";
import { Button } from "../ui/button";

const EvaluatorDashboard = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { papers, loading } = useAppSelector((state: any) => state.evaluator);

    // ... (state hooks preserved via context usually, but here we replace block)
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState<"all" | "completed" | "in-progress" | "pending">("all");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortBy, setSortBy] = useState<"date-desc" | "date-asc" | "status" | "subject">("date-desc");
    const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    // Tour state
    const [runTour, setRunTour] = useState(false);
    const tourSteps: Step[] = [
        {
            target: '.tour-dashboard-search',
            title: 'Search Papers',
            content: 'Quickly find specific evaluation papers by subject name or paper code.',
            disableBeacon: true,
        },
        {
            target: '.tour-dashboard-filters',
            title: 'Sort & Filter',
            content: 'Organize your view by date, status, or toggle between grid and list layouts.',
            disableBeacon: true,
        },
        {
            target: '.tour-dashboard-list',
            title: 'Paper Collection',
            content: 'Access all your student answer sheets here. Click on a card to start evaluating.',
            disableBeacon: true,
        }
    ];

    useEffect(() => {
        const hasSeenTour = localStorage.getItem('hasSeenEvaluatorDashboardTour');
        if (!hasSeenTour) {
            setRunTour(true);
        }
    }, []);

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status, action } = data;
        const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status) || action === ACTIONS.CLOSE) {
            setRunTour(false);
            localStorage.setItem('hasSeenEvaluatorDashboardTour', 'true');
        }
    };

    // Debounce Search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setCurrentPage(1); // Reset to first page on search
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        dispatch(fetchPaperCodes({
            limit: itemsPerPage,
            offset: (currentPage - 1) * itemsPerPage,
            search: debouncedSearchQuery || undefined
        }));
    }, [dispatch, debouncedSearchQuery, currentPage]);


    // Filter and Sort logic (Client-side Status & Sort only)
    const filteredPapers = papers
        .filter((p: PaperCode) => {
            // Search is now server-side
            const matchesStatus = statusFilter === "all" || p.status === statusFilter;
            return matchesStatus;
        })
        .sort((a: PaperCode, b: PaperCode) => {
            if (sortBy === "date-desc") return new Date(b.date).getTime() - new Date(a.date).getTime();
            if (sortBy === "date-asc") return new Date(a.date).getTime() - new Date(b.date).getTime();
            if (sortBy === "status") return a.status.localeCompare(b.status);
            if (sortBy === "subject") return a.subject.localeCompare(b.subject);
            return 0;
        });

    const handlePaperClick = (paper: PaperCode) => {
        navigate(`/evaluator/${paper.id}`);
    };

    const handleNextPage = () => {
        // Simple pagination logic assuming we get full pages until the end
        if (papers.length === itemsPerPage) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };




    return (
        <div className="min-h-full bg-muted/20">
            <div className="flex justify-between items-center px-4 pt-4">
                <Button
                    variant="outline"
                    onClick={() => navigate('/home')}
                    className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 w-fit px-3 py-2 rounded-lg hover:bg-white border border-transparent hover:border-gray-200 transition-all hover:shadow-sm"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to Home
                </Button>
                <Button
                    variant="outline"
                    onClick={() => setRunTour(true)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-primary w-fit px-3 py-2 rounded-lg hover:bg-primary/5 border border-transparent hover:border-primary/10 transition-all hover:shadow-sm"
                >
                    <HelpCircle className="w-4 h-4" /> Tutorial
                </Button>
            </div>
            <div className="flex flex-col items-center justify-center pt-12 pb-6 px-6">
                {/* Hero / Title Section */}
                <div className="text-center space-y-2 max-w-2xl">
                    <h1 className="text-3xl md:text-4xl font-black text-foreground tracking-tight">Evaluator Dashboard</h1>
                    <p className="text-muted-foreground text-lg">Manage, evaluate, and track student performance with AI-powered insights.</p>
                </div>
            </div>

            {/* Centered Search & Controls Toolbar - Sticky */}
            <div className="sticky top-4 z-40 w-full max-w-5xl mx-auto px-6 mb-8">
                <div className="w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 rounded-2xl shadow-sm border border-border p-2 flex flex-col md:flex-row items-center gap-2">
                    {/* Search Input */}
                    <div className="relative flex-1 w-full md:w-auto">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search by subject or paper code..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="tour-dashboard-search w-full pl-11 pr-4 py-3 bg-transparent border-none focus:ring-0 text-base placeholder:text-muted-foreground/70 text-foreground font-medium rounded-xl"
                        />
                    </div>

                    <div className="h-8 w-px bg-border hidden md:block" />

                    {/* Filters Group */}
                    <div className="tour-dashboard-filters flex items-center gap-2 w-full md:w-auto p-1">

                        {/* Status Filter Button */}
                        {/* Status Filter Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setIsFilterOpen(!isFilterOpen)}
                                className={`p-2.5 rounded-xl transition-colors ${statusFilter !== 'all' ? 'bg-primary/10 text-primary hover:bg-primary/20' : 'bg-muted/50 hover:bg-muted text-muted-foreground hover:text-primary'}`}
                                title={`Filter: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}`}
                            >
                                <Filter className="w-5 h-5" />
                            </button>

                            {isFilterOpen && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setIsFilterOpen(false)} />
                                    <div className="absolute top-full mt-2 left-0 w-48 bg-background border border-border shadow-xl rounded-xl p-1.5 z-50 flex flex-col gap-1 animate-in fade-in zoom-in-95 duration-200">
                                        {[
                                            { value: 'all', label: 'All Papers' },
                                            { value: 'completed', label: 'Completed' },
                                            { value: 'in-progress', label: 'In Progress' },
                                            { value: 'pending', label: 'Pending' }
                                        ].map((option) => (
                                            <button
                                                key={option.value}
                                                onClick={() => {
                                                    setStatusFilter(option.value as any);
                                                    setIsFilterOpen(false);
                                                }}
                                                className={`flex items-center justify-between w-full px-3 py-2 text-sm rounded-lg transition-colors ${statusFilter === option.value ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                                            >
                                                <span>{option.label}</span>
                                                {statusFilter === option.value && <Check className="w-4 h-4" />}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Enhanced Sort Button */}
                        <button
                            onClick={() => setSortBy(prev => {
                                if (prev === "date-desc") return "date-asc";
                                if (prev === "date-asc") return "status";
                                if (prev === "status") return "subject";
                                return "date-desc";
                            })}
                            className={`p-2.5 bg-muted/50 hover:bg-muted rounded-xl text-muted-foreground hover:text-primary transition-colors ${sortBy !== 'date-desc' ? 'text-primary bg-primary/5' : ''}`}
                            title={`Sort by: ${sortBy}`}
                        >
                            {sortBy === "date-desc" && <Calendar className="w-5 h-5" />}
                            {sortBy === "date-asc" && <Clock className="w-5 h-5" />}
                            {sortBy === "status" && <SortDesc className="w-5 h-5" />}
                            {sortBy === "subject" && <ArrowUpAZ className="w-5 h-5" />}
                        </button>

                        <button
                            onClick={() => setViewMode(prev => prev === "grid" ? "list" : "grid")}
                            className="p-2.5 bg-muted/50 hover:bg-muted rounded-xl text-muted-foreground hover:text-primary transition-colors "
                            title="Toggle View"
                        >
                            {viewMode === "grid" ? <LayoutGrid className="w-5 h-5" /> : <ListIcon className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="p-6 max-w-7xl mx-auto md:p-8 space-y-8">
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                ) : (
                    <>
                        {/* Papers Grid */}
                        <div className={`tour-dashboard-list ${viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6" : "space-y-3"}`}>
                            {filteredPapers.map((paper: PaperCode) => (
                                <div
                                    key={paper.id}
                                    onClick={() => handlePaperClick(paper)}
                                    className={`group bg-card rounded-2xl border border-border p-5 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/50 hover:-translate-y-1 transition-all duration-300 cursor-pointer relative overflow-hidden ${viewMode === "list" ? "flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6" : ""}`}
                                >
                                    {/* Decorative Gradient Blob */}
                                    <div className="absolute -right-10 -top-10 w-32 h-32 bg-gradient-to-br from-primary/5 to-gray-500/5 rounded-full blur-2xl group-hover:bg-primary/10 transition-colors" />

                                    <div className={viewMode === "list" ? "flex-1 w-full" : "space-y-6"}>

                                        {/* Content */}
                                        <div>
                                            <h3 className="font-bold text-card-foreground text-lg group-hover:text-primary transition-colors line-clamp-1" title={paper.subject}>
                                                {paper.subject}
                                            </h3>
                                            <div className="flex items-center gap-2 mt-1.5">
                                                <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded border border-border">{paper.code}</span>
                                                <span className="text-[10px] text-muted-foreground">•</span>
                                                <span className="text-xs text-muted-foreground font-medium">Batch 2024</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Footer / Meta */}
                                    <div className={`relative pt-4 z-10 ${viewMode === "list" ? "flex items-center justify-between md:justify-end gap-4 md:gap-8 w-full md:w-auto md:min-w-[33%] border-t md:border-t-0 mt-2 md:mt-0 pt-3 md:pt-0 border-border" : "pt-4 border-border flex items-center justify-between"}`}>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <TrendingUp className="w-4 h-4" />
                                            <span className="font-medium text-foreground">
                                                {paper.evaluated} / {paper.totalCopies}
                                            </span>
                                            <span className="text-xs text-[10px]">Evaluated</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                                            <Calendar className="w-3.5 h-3.5" />
                                            <span>{new Date(paper.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Empty State */}
                        {filteredPapers.length === 0 && (
                            <div className="flex flex-col items-center justify-center text-center">
                                <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-6 ring-8 ring-background">
                                    <Search className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <h3 className="text-xl font-bold text-foreground mb-2">No papers found</h3>
                                <p className="text-muted-foreground max-w-sm mx-auto">
                                    We couldn't find any papers matching your search. Try adjusting your filters or search query.
                                </p>
                                <button
                                    onClick={() => { setSearchQuery(""); setStatusFilter("all"); }}
                                    className="mt-6 px-6 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl text-sm font-semibold transition-colors shadow-lg shadow-primary/20"
                                >
                                    Clear Filters
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Pagination Controls */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                    <button
                        onClick={handlePrevPage}
                        disabled={currentPage === 1 || loading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-muted-foreground">
                        Page {currentPage}
                    </span>
                    <button
                        onClick={handleNextPage}
                        disabled={papers.length < itemsPerPage || loading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Next
                    </button>
                </div>
            </div>

            <Joyride
                steps={tourSteps}
                run={runTour}
                continuous
                showProgress
                showSkipButton
                tooltipComponent={TourTooltip}
                callback={handleJoyrideCallback}
                disableScrollParentFix={true}
                disableScrolling={true}
                floaterProps={{
                    disableAnimation: true,
                }}
                styles={{
                    spotlight: {
                        borderRadius: '16px',
                    },
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    },
                    options: {
                        zIndex: 10000,
                    }
                }}
            />
        </div>
    );
};
export default EvaluatorDashboard;
